import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Lock, Play, Clock, Eye } from 'lucide-react';
import { Btn, Alert, Chip, LoadingSpinner, MultilineText } from '@/presentation/component/ui';
import { hasCourseAccess } from '@/domain/model/accessUtils';
import { useCatalog } from '@/presentation/hook/useCatalog';
import { useAuth } from '@/presentation/hook/useAuth';
import { useSubcategoryAccess } from '@/presentation/hook/useSubcategoryAccess';
import { formatCFA } from '@/presentation/util/format';
import { getTotalCourses, getTotalDurationHours } from '@/presentation/util/catalogUtils';
import { PaymentModal } from '@/presentation/component/payment/PaymentModal';
import { MediaTypeLabel } from '@/presentation/component/course/MediaTypeLabel';

export const SubcategoryViewPage = () => {
  const { t } = useTranslation();
  const { subcategoryId } = useParams<{ subcategoryId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentSubcategory, isLoading, fetchSubcategory, clearCurrentSubcategory } = useCatalog();
  const { checkAuth } = useAuth();
  const { hasAccess, isAuthenticated } = useSubcategoryAccess(subcategoryId);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);

  useEffect(() => {
    if (subcategoryId) {
      fetchSubcategory(subcategoryId);
    }
    return () => clearCurrentSubcategory();
  }, [subcategoryId, fetchSubcategory, clearCurrentSubcategory]);

  const totalCourses = getTotalCourses(currentSubcategory?.chapters);

  const handlePurchase = () => {
    if (!isAuthenticated) {
      navigate(`/login?from=${encodeURIComponent(location.pathname)}`);
      return;
    }
    setIsPaymentModalOpen(true);
  };

  const handlePaymentModalClose = () => {
    setIsPaymentModalOpen(false);
    checkAuth();
  };

  if (isLoading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!currentSubcategory) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-[var(--color-ink)] mb-4">
            {t('category.formationNotFound')}
          </h1>
          <Link to="/formation">
            <Btn kind="primary">{t('buttons.backToFormations')}</Btn>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <nav className="mb-8">
          <ol className="flex items-center gap-2 text-sm">
            <li>
              <Link
                to="/formation"
                className="text-[var(--color-accent)] hover:text-[var(--color-accent-hover)]"
              >
                {t('nav.formations')}
              </Link>
            </li>
            {currentSubcategory.categoryId && currentSubcategory.categoryName && (
              <>
                <li className="text-[var(--color-ink-faint)]">/</li>
                <li>
                  <Link
                    to={`/category/${currentSubcategory.categoryId}`}
                    className="text-[var(--color-accent)] hover:text-[var(--color-accent-hover)]"
                  >
                    {currentSubcategory.categoryName}
                  </Link>
                </li>
              </>
            )}
            <li className="text-[var(--color-ink-faint)]">/</li>
            <li className="text-[var(--color-ink)]">{currentSubcategory.name}</li>
          </ol>
        </nav>

        <div className="mb-12">
          <h1 className="text-3xl sm:text-4xl font-bold text-[var(--color-ink)] mb-4">
            {currentSubcategory.name}
          </h1>
          <MultilineText className="text-lg sm:text-xl text-[var(--color-ink-soft)] mb-6">
            {currentSubcategory.description}
          </MultilineText>

          <div className="bg-[var(--color-accent-tint)] border border-[var(--color-accent-soft)] rounded-lg p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold text-[var(--color-ink)] mb-1">
                  {hasAccess ? t('category.accessGranted') : t('category.unlockAllCourses')}
                </h3>
                <p className="text-[var(--color-ink-soft)] mb-1">
                  {hasAccess ? t('category.accessDescription') : t('category.unlockDescription')}
                </p>
                <span className="text-sm text-[var(--color-ink-faint)]">
                  {t('coursePlayer.chapters', { count: currentSubcategory.chapters?.length || 0 })}
                  {' • '}
                  {t('category.coursesCount', { count: totalCourses })}
                  {' • '}
                  {t('common.durationHours', {
                    count: getTotalDurationHours(currentSubcategory.chapters),
                  })}
                </span>
                {!isAuthenticated && (
                  <Alert kind="warn" className="mt-3">
                    {t('coursePlayer.loginRequiredDesc')}
                  </Alert>
                )}
              </div>
              {!hasAccess && (
                <div className="text-right shrink-0">
                  <div className="mb-2">
                    <span className="text-2xl sm:text-3xl font-bold text-[var(--color-accent)]">
                      {formatCFA(currentSubcategory.price)}
                    </span>
                  </div>
                  <Btn kind="primary" size="lg" onClick={handlePurchase}>
                    {isAuthenticated ? t('buttons.purchase') : t('buttons.loginAndPurchase')}
                  </Btn>
                </div>
              )}
            </div>
          </div>
        </div>

        {currentSubcategory.chapters && currentSubcategory.chapters.length > 0 && (
          <div>
            <h2 className="text-2xl font-bold text-[var(--color-ink)] mb-6">
              {t('category.formationContent')}
            </h2>

            <div className="space-y-6">
              {currentSubcategory.chapters.map((chapter, chapterIndex) => (
                <div
                  key={chapter.id}
                  className="bg-[var(--color-surface)] rounded-lg border border-[var(--color-line)] overflow-hidden"
                >
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-8 h-8 bg-[var(--color-accent)] rounded-full flex items-center justify-center text-[var(--color-on-accent)] text-sm font-bold shrink-0">
                        {chapterIndex + 1}
                      </div>
                      <h3 className="text-xl font-semibold text-[var(--color-ink)]">
                        {chapter.name}
                      </h3>
                    </div>

                    {chapter.description && (
                      <MultilineText className="text-[var(--color-ink-soft)] mb-4">
                        {chapter.description}
                      </MultilineText>
                    )}

                    {chapter.courses && chapter.courses.length > 0 && (
                      <div className="space-y-3">
                        {chapter.courses.map((course, courseIndex) => (
                          <div
                            key={course.id}
                            className="flex items-center justify-between p-3 bg-[var(--color-surface-2)] rounded-lg gap-3"
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              <span className="text-sm font-medium text-[var(--color-ink-faint)] w-6 shrink-0">
                                {courseIndex + 1}.
                              </span>
                              <div className="min-w-0">
                                <div className="flex items-center gap-2">
                                  <h4 className="font-medium text-[var(--color-ink)] truncate">
                                    {course.title}
                                  </h4>
                                  {course.preview && (
                                    <Chip kind="accent" className="shrink-0">
                                      <Eye className="h-3 w-3 mr-1 inline" />
                                      {t('coursePlayer.freePreview')}
                                    </Chip>
                                  )}
                                </div>
                                <div className="flex items-center gap-3 text-sm text-[var(--color-ink-faint)]">
                                  <span className="flex items-center gap-1">
                                    <Clock className="h-3.5 w-3.5" />
                                    {t('common.durationMinutes', {
                                      count: Math.round(course.duration / 60),
                                    })}
                                  </span>
                                  <MediaTypeLabel mediaType={course.mediaType} />
                                </div>
                              </div>
                            </div>

                            <div className="shrink-0">
                              {hasCourseAccess(course, hasAccess) ? (
                                <Link to={`/course/${currentSubcategory.id}/${course.id}`}>
                                  <Btn
                                    size="sm"
                                    kind="primary"
                                    icon={<Play className="h-3.5 w-3.5" />}
                                  >
                                    {course.preview && !hasAccess
                                      ? t('coursePlayer.freePreview')
                                      : t('category.start')}
                                  </Btn>
                                </Link>
                              ) : !isAuthenticated ? (
                                <Link to={`/login?from=${encodeURIComponent(location.pathname)}`}>
                                  <Btn size="sm" icon={<Lock className="h-3.5 w-3.5" />}>
                                    {t('buttons.signIn')}
                                  </Btn>
                                </Link>
                              ) : (
                                <Btn size="sm" disabled icon={<Lock className="h-3.5 w-3.5" />}>
                                  {t('coursePlayer.purchaseRequired')}
                                </Btn>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {(!currentSubcategory.chapters || currentSubcategory.chapters.length === 0) && (
          <div className="text-center py-12">
            <div className="max-w-md mx-auto">
              <h3 className="text-lg font-semibold text-[var(--color-ink)] mb-2">
                {t('category.preparingContent')}
              </h3>
              <p className="text-[var(--color-ink-faint)]">{t('category.preparingContentDesc')}</p>
            </div>
          </div>
        )}
      </div>

      <PaymentModal
        open={isPaymentModalOpen}
        onClose={handlePaymentModalClose}
        subcategory={currentSubcategory}
      />
    </div>
  );
};
